import React, { useCallback, useMemo, useState } from 'react';
import type { ColDef, ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import { Plus } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { AddForwardInterestSettingModal } from './AddForwardInterestSettingModal';
import { useCrcies } from '../../hooks/useCrcies';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import {
  getForwardInterestSettings,
  saveForwardInterestSettingsBatch,
} from '../../api/forwardInterestSettings';
import type {
  ForwardInterestSetting,
  ForwardInterestSettingNew,
} from '../../types/ForwardInterestSetting';
import { t } from '../../i18n/t';
import './ForwardInterestSettingsPage.css';

const RESOURCE_CODE = 'forwardInterestSettings';

export function ForwardInterestSettingsPage() {
  const { user } = useAuth();
  const canEdit = canEditUtil(user, RESOURCE_CODE);

  const { data: crcies, loading: crciesLoading, error: crciesError } = useCrcies();

  // Bumped after the wizard creates a row, so getRows gets a new identity and
  // useCrudGrid re-fetches (mirrors the SpecialRates range-add recipe).
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);

  const wrappedGetRows = useCallback(
    () => getForwardInterestSettings(),
    // refreshVersion intentionally part of the identity so a wizard add re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshVersion],
  );

  const saveBatch = useCallback(
    (
      added: ForwardInterestSetting[],
      modified: ForwardInterestSetting[],
      deletedIds: unknown[],
    ) =>
      // Grid-native "added" is always empty here — the wizard is the only way
      // to create a row (showAdd={false} below), and it submits directly via
      // handleWizardSubmit rather than through useCrudGrid's dirty tracking.
      saveForwardInterestSettingsBatch(added, modified, deletedIds as number[]),
    [],
  );

  const columnDefs = useMemo<ColDef<ForwardInterestSetting>[]>(() => {
    if (!crcies) return [];

    const crcyValues = crcies.map((c) => `${c.crcyId} - ${c.crcyName}`);
    const crcyLabelById = new Map<number, string>(
      crcies.map((c) => [c.crcyId, `${c.crcyId} - ${c.crcyName}`]),
    );
    const crcyIdByLabel = new Map<string, number>(
      crcies.map((c) => [`${c.crcyId} - ${c.crcyName}`, c.crcyId]),
    );

    return [
      {
        field: 'assetId',
        headerName: t('forwardInterestSettings.columns.assetId'),
        width: 110,
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        // Read-only projection off the underlying ASSETS row — the ticker
        // lives there, not in this table. Created together with the settings
        // row by the wizard; edited via the asset-form screen, not here.
        field: 'assetName',
        headerName: t('forwardInterestSettings.columns.assetName'),
        flex: 1,
        minWidth: 160,
        editable: false,
      },
      {
        field: 'ticker',
        headerName: t('forwardInterestSettings.columns.ticker'),
        width: 130,
        editable: false,
      },
      {
        field: 'days',
        headerName: t('forwardInterestSettings.columns.days'),
        width: 110,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'period',
        headerName: t('forwardInterestSettings.columns.period'),
        flex: 1,
        minWidth: 140,
        editable: true,
      },
      {
        field: 'ledCrcyId',
        headerName: t('forwardInterestSettings.columns.ledCrcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: crcyValues },
        valueGetter: (p: ValueGetterParams<ForwardInterestSetting>) => {
          if (!p.data) return '';
          return crcyLabelById.get(p.data.ledCrcyId) ?? String(p.data.ledCrcyId);
        },
        valueSetter: (p: ValueSetterParams<ForwardInterestSetting>) => {
          if (!p.data) return false;
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.ledCrcyId = newId;
          return true;
        },
      },
      {
        field: 'leaderCrcyId',
        headerName: t('forwardInterestSettings.columns.leaderCrcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: crcyValues },
        valueGetter: (p: ValueGetterParams<ForwardInterestSetting>) => {
          if (!p.data) return '';
          return crcyLabelById.get(p.data.leaderCrcyId) ?? String(p.data.leaderCrcyId);
        },
        valueSetter: (p: ValueSetterParams<ForwardInterestSetting>) => {
          if (!p.data) return false;
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.leaderCrcyId = newId;
          return true;
        },
      },
    ];
  }, [crcies]);

  const handleWizardSubmit = useCallback(
    async (payload: ForwardInterestSettingNew) => {
      setSubmitting(true);
      setWizardError(null);
      try {
        await saveForwardInterestSettingsBatch([payload], [], []);
        setRefreshVersion((v) => v + 1);
        setWizardOpen(false);
      } catch (err: unknown) {
        setWizardError(err instanceof Error ? err.message : String(err));
      } finally {
        setSubmitting(false);
      }
    },
    [],
  );

  if (crciesError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('forwardInterestSettings.errorPrefix', { message: crciesError })}
        </div>
      </div>
    );
  }

  if (crciesLoading || !crcies) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <div className="forward-interest-settings">
      <div className="forward-interest-settings__toolbar">
        <button
          type="button"
          className="forward-interest-settings__add"
          onClick={() => setWizardOpen(true)}
          disabled={!canEdit}
          title={t('forwardInterestSettings.wizard.addButton')}
          aria-label={t('forwardInterestSettings.wizard.addButton')}
        >
          <Plus size={16} />
          <span>{t('forwardInterestSettings.wizard.addButton')}</span>
        </button>
      </div>
      <div className="forward-interest-settings__crud">
        <LopCrudPage<ForwardInterestSetting>
          resourceCode={RESOURCE_CODE}
          titleKey="forwardInterestSettings.pageTitle"
          idField="assetId"
          getRows={wrappedGetRows}
          saveBatch={saveBatch}
          columnDefs={columnDefs}
          showAdd={false}
        />
      </div>
      {wizardOpen && (
        <AddForwardInterestSettingModal
          crcies={crcies}
          submitting={submitting}
          submitError={wizardError}
          onSubmit={(payload) => {
            void handleWizardSubmit(payload);
          }}
          onClose={() => {
            setWizardOpen(false);
            setWizardError(null);
          }}
        />
      )}
    </div>
  );
}
